/**
 * PDF Upload Item
 * Displays individual PDF status in upload list
 */

import React from 'react';
import {
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ScheduleIcon from '@mui/icons-material/Schedule';
import type { PDFUploadState } from '../../hooks/useInsuranceReport';

interface PDFUploadItemProps {
  pdf: PDFUploadState;
}

const PDFUploadItem: React.FC<PDFUploadItemProps> = ({ pdf }) => {
  const getStatusIcon = () => {
    switch (pdf.status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      case 'uploading':
      case 'processing':
        return <CircularProgress size={24} />;
      default:
        return <ScheduleIcon />;
    }
  };

  const getStatusColor = (): 'default' | 'primary' | 'success' | 'error' | 'warning' => {
    switch (pdf.status) {
      case 'completed':
        return 'success';
      case 'error':
        return 'error';
      case 'uploading':
      case 'processing':
        return 'primary';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (): string => {
    switch (pdf.status) {
      case 'completed':
        return `${pdf.findingsExtracted || 0} findings extracted`;
      case 'error':
        return pdf.error || 'Failed';
      case 'uploading':
        return 'Uploading...';
      case 'processing':
        return 'Processing...';
      default:
        return 'Pending';
    }
  };

  return (
    <ListItem>
      <ListItemIcon>{getStatusIcon()}</ListItemIcon>
      <ListItemText
        primary={pdf.filename}
        secondary={pdf.file ? `${(pdf.file.size / (1024 * 1024)).toFixed(2)} MB` : undefined}
      />
      <Chip label={getStatusLabel()} color={getStatusColor()} size="small" />
    </ListItem>
  );
};

export default PDFUploadItem;
